import { ALL_AI_CRAWLER_PROVIDERS } from '../shared';
import type { DiagnosticsReportListItem, DiagnosticsReportResponse } from './api/diagnosticsReportApi';
import {
  extractDomain,
  getBlockedProviders,
  getCheckRows,
  getCrawlersTested,
  mapUserAgentToProvider,
} from './diagnosticsReportUtils';
import { VISIBLE_STEPS } from './diagnosticsStepMap';

export type DiagnosticsRunTrigger =
  | 'diagnostics_banner'
  | 'progress_modal_error_state'
  | 'diagnostics_report_table'
  | 'report_page_rerun_diagnostics';

export type DiagnosticsFailureReason = 'connection_drop' | 'internal_error';

type DiagnosticsReportAnalyticsProperties = {
  report_id: string;
  domain: string;
  missing_bots_flagged: number;
  bots_tested: string;
  bots_blocked: string;
  bots_allowed: string;
  report_generated_timestamp: string;
  url_tested: string;
};

type DiagnosticsCheckAnalyticsProperties = Omit<DiagnosticsReportAnalyticsProperties, 'report_generated_timestamp'> & {
  check: string;
};

export type DiagnosticsAnalyticsEventProperties = {
  'Diagnostics: Tab Viewed': void;
  'Diagnostics: Progress Step': {
    request_id: string;
    domain: string;
    url: string;
    step_index: number;
    step_name: string;
    step_status: 'passed';
    elapsed_ms: number;
  };
  'Diagnostics: Result': {
    request_id: string;
    domain: string;
    url: string;
    bots_tested: string;
    bots_blocked: string;
    bots_allowed: string;
    report_id: string;
    diagnostics_status: 'completed' | 'error';
    duration_ms: number;
    attempts: number;
    failure_reason: '' | DiagnosticsFailureReason;
    // Absent rather than empty when the run found nothing to report: an optional key
    // is dropped by JSON.stringify, so Mixpanel never receives an empty list.
    blocked_checks?: string[];
    blocked_at?: string[];
  };
  'Diagnostics: Check Failed To Run': {
    request_id: string;
    check_name: string;
    check_error: 'check_did_not_run';
    affected_bots: string;
  };
  'Diagnostics: Run Clicked': {
    domain: string;
    bots_to_test: string;
    run_trigger: DiagnosticsRunTrigger;
    request_id: string;
  };
  'Diagnostics: Report Clicked': DiagnosticsReportAnalyticsProperties;
  'Diagnostics: Report Viewed': DiagnosticsReportAnalyticsProperties;
  'Diagnostics: Fix Suggestion Viewed': DiagnosticsCheckAnalyticsProperties;
  'Diagnostics: Fix Suggestion Closed': DiagnosticsCheckAnalyticsProperties & {
    report_generated_timestamp: string;
  };
  'Diagnostics: Fix Tip Doc Link Clicked': DiagnosticsCheckAnalyticsProperties & {
    link_url: string;
  };
  'Diagnostics: Report Exported': {
    report_id: string;
    trigger: 'report_page' | 'report_table';
  };
};

type ReportLike = Pick<DiagnosticsReportResponse, 'id' | 'url' | 'userAgents' | 'createdAt'> | Pick<DiagnosticsReportListItem, 'id' | 'url' | 'userAgentResults' | 'createdAt'>;
export type DiagnosticsAnalyticsEvent = keyof DiagnosticsAnalyticsEventProperties;

export function createDiagnosticsRequestId(): string {
  return crypto.randomUUID();
}

function getProviderSortWeight(provider: string): number {
  const index = ALL_AI_CRAWLER_PROVIDERS.indexOf(provider as typeof ALL_AI_CRAWLER_PROVIDERS[number]);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function sortProviders(providers: string[]): string[] {
  return [...providers].sort((left, right) => {
    const leftWeight = getProviderSortWeight(left);
    const rightWeight = getProviderSortWeight(right);

    if (leftWeight === rightWeight) {
      return left.localeCompare(right);
    }

    return leftWeight - rightWeight;
  });
}

// Keys match CheckRow.checkKey from getCheckRows; values are the event vocabulary. The key
// order is the emission order of blocked_checks and of the check part of blocked_at, so it
// mirrors the check order the report itself uses.
const CHECK_TOKENS: Record<string, string> = {
  Integration: 'prerender_integration',
  'robots.txt': 'robots_txt',
  CDN: 'cdn',
  WAF: 'waf',
};

// The blocks the report can stand behind, per check: getCheckRows drops blocked crawlers
// for a check that could not finish, so an untrustworthy check never reports a block here.
function deriveStrictBlocks(userAgents: DiagnosticsReportResponse['userAgents']): {
  blockedChecks: string[];
  blockedAt: string[];
} {
  const rowsByCheckKey = new Map(getCheckRows(userAgents).map((row) => [row.checkKey, row] as const));
  const blockedChecks: string[] = [];
  const blockedAt: string[] = [];

  for (const [checkKey, checkToken] of Object.entries(CHECK_TOKENS)) {
    const blockedProviders = rowsByCheckKey.get(checkKey)?.blockedProviders ?? [];

    if (blockedProviders.length === 0) {
      continue;
    }

    blockedChecks.push(checkToken);

    for (const provider of ALL_AI_CRAWLER_PROVIDERS.filter((candidate) => blockedProviders.includes(candidate))) {
      blockedAt.push(`${checkToken}:${provider.toLowerCase()}`);
    }
  }

  return { blockedChecks, blockedAt };
}

export function getDiagnosticsStepName(stepKey: string): string {
  return VISIBLE_STEPS.find((step) => step.key === stepKey)?.label ?? stepKey;
}

export function buildDiagnosticsReportViewedProperties(report: ReportLike): DiagnosticsReportAnalyticsProperties {
  let botsTested: string[];
  let botsBlocked: string[];

  if ('userAgents' in report) {
    botsTested = sortProviders(getCrawlersTested(report.userAgents));
    botsBlocked = sortProviders(getBlockedProviders(report.userAgents));
  } else {
    botsTested = sortProviders(report.userAgentResults.map((r) => mapUserAgentToProvider(r.shortName)));
    botsBlocked = sortProviders(report.userAgentResults.filter((r) => r.result === 'failed').map((r) => mapUserAgentToProvider(r.shortName)));
  }

  const botsAllowed = botsTested.filter((provider) => !botsBlocked.includes(provider));

  return {
    report_id: report.id,
    domain: extractDomain(report.url),
    missing_bots_flagged: botsBlocked.length,
    bots_tested: botsTested.join(', '),
    bots_blocked: botsBlocked.join(', '),
    bots_allowed: botsAllowed.join(', '),
    report_generated_timestamp: report.createdAt,
    url_tested: report.url,
  };
}

export function buildDiagnosticsResultProperties({
  attempts,
  domain,
  duration_ms,
  failure_reason,
  request_id,
  report_id,
  url,
  userAgents,
}: {
  attempts: number;
  domain: string;
  duration_ms: number;
  failure_reason: '' | DiagnosticsFailureReason;
  request_id: string;
  report_id: string;
  url: string;
  userAgents?: DiagnosticsReportResponse['userAgents'];
}): DiagnosticsAnalyticsEventProperties['Diagnostics: Result'] {
  const botsTested = userAgents ? sortProviders(getCrawlersTested(userAgents)) : [];
  const botsBlocked = userAgents ? sortProviders(getBlockedProviders(userAgents)) : [];
  const botsAllowed = botsTested.filter((provider) => !botsBlocked.includes(provider));
  const diagnostics_status = failure_reason ? 'error' : 'completed';
  // A run that failed reports no blocks at all: its checks are as untrustworthy as the
  // run itself, the same way botsTested/botsBlocked ignore a missing crawler result.
  const strictBlocks = userAgents && !failure_reason ? deriveStrictBlocks(userAgents) : undefined;

  return {
    request_id,
    domain,
    url,
    bots_tested: botsTested.join(', '),
    bots_blocked: botsBlocked.join(', '),
    bots_allowed: botsAllowed.join(', '),
    report_id,
    diagnostics_status,
    duration_ms,
    attempts,
    failure_reason,
    blocked_checks: strictBlocks?.blockedChecks.length ? strictBlocks.blockedChecks : undefined,
    blocked_at: strictBlocks?.blockedAt.length ? strictBlocks.blockedAt : undefined,
  };
}

export function buildFailedCheckToRunProperties({
  request_id,
  userAgents,
}: {
  request_id: string;
  userAgents?: DiagnosticsReportResponse['userAgents'];
}): DiagnosticsAnalyticsEventProperties['Diagnostics: Check Failed To Run'][] {
  const agents = userAgents ? Object.entries(userAgents) : [];
  const affectedBots = sortProviders(agents.map(([agentName]) => mapUserAgentToProvider(agentName))).join(', ');

  return VISIBLE_STEPS.flatMap((step) => {
    const isMissingForAllAgents = agents.length > 0 && agents.every(([, checks]) => checks[step.key] === undefined);

    if (!isMissingForAllAgents) {
      return [];
    }

    return [
      {
        request_id,
        check_name: step.label,
        check_error: 'check_did_not_run' as const,
        affected_bots: affectedBots,
      },
    ];
  });
}
